export type BookingMethod = 'phone' | 'email' | 'whatsapp' | 'other_platform' | 'paper';

export type Scenario = 'conservative' | 'moderate' | 'optimistic';

export const SCENARIO_MULTIPLIERS: Record<Scenario, number> = {
  conservative: 0.7,
  moderate: 1.0,
  optimistic: 1.3,
};

export interface RoiInputs {
  courts: number;
  rate: number;
  hoursPerDay: number;
  noShowPercent: number;
  bookingMethod: BookingMethod;
  // Members
  activeMembers: number;
  monthlyRevenuePerMember: number;
  // Booking channel
  phoneBookingsPerDay: number;
  // Advanced
  monthlyChurnRate: number;
  coaches: number;
  coachingSessionsPerWeek: number;
  // Scenario
  scenario: Scenario;
}

export interface RoiResults {
  annualNoShowLoss: number;
  recoveredRevenue: number;
  bookedRevenue: number;
  dailyBookings: number;
  weeklyHoursSaved: number;
  annualHoursSaved: number;
  waitlistRevenue: number;
  memberRetention: number;
  bookingAutomation: number;
  totalAnnualImpact: number;
  revenueUpliftPercent: number;
  // Intermediates for formula display (guaranteed to match the calculation)
  annualChurnLoss: number;
  staffTimeSaved: number;
  generalAdminSavings: number;
  coachingUplift: number;
  rawTotal: number; // pre-scenario-multiplier sum
  // Monthly equivalents for display
  monthlyNoShow: number;
  monthlyWaitlist: number;
  monthlyRetention: number;
  monthlyAutomation: number;
  monthlyTotalImpact: number;
}

// Realistic per-booking admin time by method (minutes).
// Phone: pick up, check calendar, confirm name/slot, note it = ~5 min.
// These are what the club SAVES per booking when switching to self-serve.
const MINUTES_SAVED_PER_BOOKING: Record<BookingMethod, number> = {
  phone: 5,
  email: 3,
  whatsapp: 3,
  other_platform: 1,
  paper: 8,
};

// No-show recovery:
// Prepayment alone reduces no-shows by 40-50% (Mindbody, foreUP 10M+ rounds).
// We use 45% — the low end of prepayment-only. We do NOT stack reminder effects
// (the Cochrane 38% is from healthcare appointment reminders, not sports).
export const NO_SHOW_REDUCTION = 0.45;

export const AVG_OCCUPANCY = 0.6;

// Waitlist fill rate: what % of empty slots get filled by auto-waitlist notifications.
// Industry claims range 10-20%, but real-world adoption is lower for sports venues
// where last-minute availability competes with player schedules. 5% is defensible.
export const WAITLIST_FILL_RATE = 0.05;

export const DAYS_PER_YEAR = 350; // excludes holidays, maintenance, closures
export const WEEKS_PER_YEAR = 52;
const DAYS_PER_WEEK = 7;

// Member retention — social features + reminders reduce churn.
// ClassPass 2025: group members 56% less likely to cancel.
// We claim 10% overall churn reduction (not 56%) because not all members
// switch to group play. 10% is the blended effect across the full member base.
export const CHURN_REDUCTION_RATE = 0.1;

// Booking automation
export const PHONE_AUTOMATION_RATE = 0.6; // 60% of phone callers will adopt self-serve
export const ADMIN_HOURLY_COST_EUR = 12;
export const ADMIN_HOURS_PER_COURT_PER_WEEK = 1.5; // scales with club size

// Coaching — online booking fills 10% more slots than phone/walk-in.
// Conservative: better visibility + instant booking removes friction.
export const COACHING_ONLINE_BOOKING_UPLIFT = 0.1;

export function calculateRoi(inputs: RoiInputs): RoiResults {
  const { courts, rate, hoursPerDay, noShowPercent, bookingMethod } = inputs;

  // ── No-show recovery ──
  // No-show rate applies to BOOKED slots, not total capacity
  const bookedRevenue = courts * rate * hoursPerDay * DAYS_PER_YEAR * AVG_OCCUPANCY;
  const annualNoShowLoss = bookedRevenue * (noShowPercent / 100);
  const recoveredRevenue = annualNoShowLoss * NO_SHOW_REDUCTION;

  // ── Admin time saved ──
  // Only count the phone bookings the user actually reports, not all bookings.
  // Phone calls take ~5 min each regardless of what the "primary" booking method is.
  const dailyBookings = courts * hoursPerDay * AVG_OCCUPANCY;
  const PHONE_MINUTES_PER_BOOKING = 5;
  const weeklyHoursSaved =
    (inputs.phoneBookingsPerDay * PHONE_MINUTES_PER_BOOKING * DAYS_PER_WEEK) / 60;
  const annualHoursSaved = weeklyHoursSaved * WEEKS_PER_YEAR;

  // ── Waitlist utilization boost ──
  const emptySlotRate = 1 - AVG_OCCUPANCY;
  const recoverableSlots =
    courts * hoursPerDay * DAYS_PER_YEAR * emptySlotRate * WAITLIST_FILL_RATE;
  const waitlistRevenue = recoverableSlots * rate;

  // ── Member retention ──
  // Annual MRR leakage from churn, partially recovered via engagement features
  const annualChurnLoss =
    inputs.activeMembers *
    inputs.monthlyRevenuePerMember *
    (inputs.monthlyChurnRate / 100) *
    12;
  const memberRetention = annualChurnLoss * CHURN_REDUCTION_RATE;

  // ── Booking automation ──
  // 1. Staff time saved by converting phone bookings to self-serve
  const phoneHoursPerYear =
    (inputs.phoneBookingsPerDay * 5 * DAYS_PER_YEAR) / 60; // 5 min per phone booking
  const staffTimeSaved =
    phoneHoursPerYear * PHONE_AUTOMATION_RATE * ADMIN_HOURLY_COST_EUR;
  // 2. General admin automation (scheduling, reminders, invoicing) — scales with club size
  const generalAdminHoursPerWeek = ADMIN_HOURS_PER_COURT_PER_WEEK * courts;
  const generalAdminSavings =
    generalAdminHoursPerWeek * ADMIN_HOURLY_COST_EUR * WEEKS_PER_YEAR;
  // 3. Coaching uplift (if applicable)
  let coachingUplift = 0;
  if (inputs.coaches > 0) {
    coachingUplift =
      inputs.coaches *
      inputs.coachingSessionsPerWeek *
      COACHING_ONLINE_BOOKING_UPLIFT *
      rate *
      WEEKS_PER_YEAR;
  }
  const bookingAutomation = staffTimeSaved + generalAdminSavings + coachingUplift;

  // ── Apply scenario multiplier ──
  const multiplier = SCENARIO_MULTIPLIERS[inputs.scenario ?? 'moderate'];

  const rawTotal = recoveredRevenue + waitlistRevenue + memberRetention + bookingAutomation;
  const totalAnnualImpact = rawTotal * multiplier;

  // Revenue uplift as a % of booked court revenue — sanity check for credibility
  const revenueUpliftPercent =
    bookedRevenue > 0 ? (totalAnnualImpact / bookedRevenue) * 100 : 0;

  // Monthly equivalents for display
  const monthlyNoShow = recoveredRevenue / 12;
  const monthlyWaitlist = waitlistRevenue / 12;
  const monthlyRetention = memberRetention / 12;
  const monthlyAutomation = bookingAutomation / 12;
  const monthlyTotalImpact = totalAnnualImpact / 12;

  return {
    annualNoShowLoss,
    recoveredRevenue,
    bookedRevenue,
    dailyBookings,
    weeklyHoursSaved,
    annualHoursSaved,
    waitlistRevenue,
    memberRetention,
    bookingAutomation,
    totalAnnualImpact,
    revenueUpliftPercent,
    annualChurnLoss,
    staffTimeSaved,
    generalAdminSavings,
    coachingUplift,
    rawTotal,
    // Monthly
    monthlyNoShow,
    monthlyWaitlist,
    monthlyRetention,
    monthlyAutomation,
    monthlyTotalImpact,
  };
}
